import React, { useState } from 'react';
import dayjs from 'dayjs';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { ShareIcon, Copy, Mail } from 'lucide-react';

import { useProperty, type ILead, type IProperty } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { useNotifications } from 'src/notifications';
import { LeadUtils } from 'src/utils';
import { copyToClipboard } from 'src/components/copy-text-button/utils';
import { formatLeadShareText, getShareLeadMailtoLink } from 'src/components/lead-sidebar/share-utils';
import analyticsClient, { AnalyticsEvent } from 'src/services/analytics';

import styles from 'src/components/lead-sidebar/ShareLeadButton.module.css';

type Props = {
    lead: ILead;
    tours: IShowing[];
    /**
     * If the lead is associated with a specific property
     */
    property?: IProperty | Pick<IProperty, 'id' | 'name'>;
};

export default function ShareLeadButton({ lead, tours, property }: Props) {
    const { userSettings } = useAuthenticatedState();
    const { addNotification } = useNotifications();
    const { data: propertyWithFacts } = useProperty(property?.id, { include_facts: true });
    const onsiteTeamFact = propertyWithFacts?.facts?.onsite_team;

    const [anchorEl, setAnchorEl] = useState<HTMLElement>();

    const upcomingTour = tours.find((tour) => {
        return tour.lead_uuid === lead.uuid &&
            !dayjs(tour.start_time).isBefore(dayjs());
    });

    const shareParams = {
        lead,
        leadName: LeadUtils.getLeadName(lead),
        propertyName: property?.name,
        upcomingTour,
        timezoneOverride: userSettings.timezone_override,
    };

    // Only enabled when the onsite_team fact is loaded (see `disabled` below).
    const mailtoHref = onsiteTeamFact ?
        getShareLeadMailtoLink({ ...shareParams, onsiteTeam: onsiteTeamFact.value }) :
        undefined;

    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        analyticsClient.track(AnalyticsEvent.CLICKED_SHARE_LEAD, { lead_uuid: lead.uuid });
        setAnchorEl(event.currentTarget);
    };

    const handleCopyToClipboard = async () => {
        const text = formatLeadShareText(shareParams);

        const isSuccess = await copyToClipboard(text);
        analyticsClient.track(AnalyticsEvent.COPIED_LEAD_TO_CLIPBOARD, {
            lead_uuid: lead.uuid,
            success: isSuccess,
        });

        if (isSuccess) {
            addNotification('Lead details copied to clipboard', 'success');
        } else {
            addNotification('Failed to copy lead details', 'error');
        }

        setAnchorEl(undefined);
    };

    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                fullWidth
                className={styles.shareBtn}
                onClick={handleOpenMenu}
                startIcon={<ShareIcon size={16} />}
                data-testid="share-lead-btn"
            >
                Share
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={() => setAnchorEl(undefined)}
            >
                <MenuItem
                    onClick={handleCopyToClipboard}
                    className={styles.menuItem}
                    data-testid="share-lead-copy-to-clipboard"
                >
                    <Copy size={16} className={styles.menuIcon} />
                    <Typography variant="body2" className={styles.menuLabel}>
                        Copy to Clipboard
                    </Typography>
                </MenuItem>
                <MenuItem
                    component={Link}
                    href={mailtoHref}
                    className={styles.menuItem}
                    data-testid="email-lead-link"
                    disabled={!onsiteTeamFact}
                >
                    <Mail size={16} className={styles.menuIcon} />
                    <Typography variant="body2" className={styles.menuLabel}>
                        Email to Onsite Team
                    </Typography>
                </MenuItem>
            </Menu>
        </>
    );
}
